import { useState, useEffect } from "react";
import jsPDF from "jspdf";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

const CATEGORY_COLORS: Record<string, string> = {
  Process: "#FF9800",
  Room: "#03A9F4",
  General: "#9C27B0",
  Extra: "#F44336"
};

const DEFAULT_CHECKLIST = [
  {
    title: "ARRIVAL",
    category: "Process",
    items: [
      { text: "Check damage and notice", isDefault: true, checked: true },
      { text: "Check for personal belongings left behind", isDefault: true, checked: true },
      { text: "Open windows to air out (if needed)", isDefault: true, checked: true },
      { text: "Start washing", isDefault: true, checked: true },
      { text: "Check supplies for restock", isDefault: true, checked: true },
    ]
  },
  {
    title: "LEAVING SPACE",
    category: "Process",
    items: [
      { text: "Take photos of the finished job", isDefault: true, checked: true },
      { text: "Empty washing machine, dryer, lint trap", isDefault: true, checked: true },
      { text: "Empty vacuum", isDefault: true, checked: true },
      { text: "Empty garbage bins", isDefault: true, checked: true },
      { text: "Turn off all lights and appliances", isDefault: true, checked: true },
      { text: "Secure the front door and any lockable storage areas", isDefault: true, checked: true },
    ]
  },
  {
    title: "GENERAL",
    category: "General",
    items: [
      { text: "Dust surfaces", isDefault: true, checked: true },
      { text: "Check for stains or spills on furniture", isDefault: true, checked: true },
      { text: "Clean mirrors, glass, and chrome fixtures", isDefault: true, checked: true },
      { text: "Clean under beds and sofas", isDefault: true, checked: true },
      { text: "Organize curtains", isDefault: true, checked: true },
      { text: "Vacuum floor", isDefault: true, checked: true },
      { text: "Mop floor", isDefault: true, checked: true },
      { text: "Restock for guests: coffee, tea, sugar, salt, pepper, coffee filters, oil, dish soap, hand soap, dishwasher pods, garbage bags", isDefault: true, checked: true },
    ]
  },
  {
    title: "KITCHEN",
    category: "Room",
    items: [
      { text: "Clean fridge", isDefault: true, checked: true },
      { text: "Throw away leftover food", isDefault: true, checked: true },
      { text: "Clean microwave inside & out", isDefault: true, checked: true },
      { text: "Clean sink & faucet", isDefault: true, checked: true },
      { text: "Clean coffeemaker", isDefault: true, checked: true },
      { text: "Clean oven", isDefault: true, checked: true },
      { text: "Restock soap", isDefault: true, checked: true },
      { text: "Wipe countertops", isDefault: true, checked: true },
      { text: "Load or empty dishwasher", isDefault: true, checked: true },
      { text: "Wash and organize dishes and cutlery", isDefault: true, checked: true },
      { text: "Restock and fold paper towel", isDefault: true, checked: true },
    ]
  },
  {
    title: "LIVING ROOM",
    category: "Room",
    items: [
      { text: "Organize chairs and decor", isDefault: true, checked: true },
      { text: "Fluff & arrange pillows", isDefault: true, checked: true },
      { text: "Check if the TV remote works", isDefault: true, checked: true },
      { text: "Wipe the table and chairs on the balcony", isDefault: true, checked: true },
      { text: "Empty the ashtray", isDefault: true, checked: true },
    ]
  },
  {
    title: "BEDROOM",
    category: "Room",
    items: [
      { text: "Replace linens", isDefault: true, checked: true },
      { text: "Make bed", isDefault: true, checked: true },
      { text: "Replace towels", isDefault: true, checked: true },
      { text: "Wipe down nightstands", isDefault: true, checked: true },
      { text: "Check the closet", isDefault: true, checked: true },
    ]
  },
  {
    title: "BATHROOM",
    category: "Room",
    items: [
      { text: "Clean mirror, chrome fixtures", isDefault: true, checked: true },
      { text: "Clean shower/tub, shower glass", isDefault: true, checked: true },
      { text: "Disinfect sink & faucet", isDefault: true, checked: true },
      { text: "Sanitize toilet", isDefault: true, checked: true },
      { text: "Restock and fold toilet paper", isDefault: true, checked: true },
      { text: "Restock soap and shampoo", isDefault: true, checked: true },
      { text: "Replace towels", isDefault: true, checked: true },
      { text: "Organize hair dryer", isDefault: true, checked: true },
    ]
  },
  {
    title: "EXTRA",
    category: "Extra",
    items: []
  },
];

export default function Checklist() {
  const [checklist, setChecklist] = useState(DEFAULT_CHECKLIST);
  const [newItems, setNewItems] = useState<Record<string, string>>({});
  const [alertVisible, setAlertVisible] = useState(false);
  const [clientInfo, setClientInfo] = useState({
    contactPerson: "",
    phone: "",
    email: "",
    property: "",
    accessCode: "",
    jobDate: "",
    startTime: "",
    endTime: "",
    beds: "",
    baths: "",
    package: "",
    cleaningType: "",
    teamAmount: "",
    description: ""
  });
  const [customFields, setCustomFields] = useState<Array<{ id: number; name: string; value: string }>>([]);

  useEffect(() => {
    document.title = "Cleaning Checklist | BNB Cleaning";
    window.scrollTo(0, 0);
  }, []);

  const handleToggle = (roomIndex: number, itemIndex: number) => {
    const updated = [...checklist];
    updated[roomIndex].items[itemIndex].checked = !updated[roomIndex].items[itemIndex].checked;
    setChecklist(updated);
  };

  const handleAdd = (roomIndex: number) => {
    const room = checklist[roomIndex];
    const newItem = newItems[room.title]?.trim();
    if (!newItem) return;
    const updated = [...checklist];
    updated[roomIndex].items.push({ text: newItem, isDefault: false, checked: false });
    setChecklist(updated);
    setNewItems({ ...newItems, [room.title]: "" });
  };

  const handleDelete = (roomIndex: number, itemIndex: number) => {
    const updated = [...checklist];
    if (updated[roomIndex].items[itemIndex].isDefault) return;
    updated[roomIndex].items.splice(itemIndex, 1);
    setChecklist(updated);
  };

  const handleAddCustomField = () => {
    const newId = customFields.length > 0 ? Math.max(...customFields.map(f => f.id)) + 1 : 1;
    setCustomFields([...customFields, { id: newId, name: "", value: "" }]);
  };

  const handleUpdateCustomField = (id: number, field: 'name' | 'value', newValue: string) => {
    setCustomFields(customFields.map(f => f.id === id ? { ...f, [field]: newValue } : f));
  };

  const handleDeleteCustomField = (id: number) => {
    setCustomFields(customFields.filter(f => f.id !== id));
  };

  const handleDownloadPDF = () => {
    const dateNow = new Date().toString().split(' GMT')[0];
    const doc = new jsPDF();
    const lineHeight = 7;
    const pageHeight = doc.internal.pageSize.height;
    let y = 10;

    const img = new Image();
    img.src = "/favicon.png"; // logo from public

    img.onload = () => {
      //Date
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(dateNow, 160, y);

      // Logo
      doc.addImage(img, "PNG", 10, y, 10, 10);

      // Djinni Cleaning (bold + colored)
      doc.setFontSize(16);
      doc.setTextColor("#FF385C");
      doc.setFont("helvetica", "bold");
      doc.text("BNB Cleaning", 25, y + 8);

      y += 15;

      // Contact info
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`437-688-4752 | ${process.env.REACT_APP_EMAIL} | www.bnb.cleaning`, 10, y);
      // doc.text("Email: djinni.cleaning@gmail.com", 80, y);
      y += 10;

      // Client info lines
      doc.setFontSize(12);
      doc.text(`Contact Person: ${clientInfo.contactPerson}`, 10, y);
      y += lineHeight;
      doc.text(`Phone: ${clientInfo.phone} | Email: ${clientInfo.email}`, 10, y);
      y += lineHeight;
      doc.text(`Property Address: ${clientInfo.property}`, 10, y);
      y += lineHeight;
      doc.text(`Job Date: ${clientInfo.jobDate} | Time: ${clientInfo.startTime} - ${clientInfo.endTime}`, 10, y);
      y += lineHeight;
      doc.text(`Access Code/Key: ${clientInfo.accessCode}`, 10, y);
      y += lineHeight;
      doc.text(`Beds: ${clientInfo.beds} | Baths: ${clientInfo.baths}`, 10, y);
      y += lineHeight;
      doc.text(`Cleaning Type: ${clientInfo.cleaningType} | Package: ${clientInfo.package}`, 10, y);
      y += lineHeight;
      if (clientInfo.description) {
        doc.text(`Description: ${clientInfo.description}`, 10, y);
        y += lineHeight;
      }
      customFields.forEach((field) => {
        if (field.name && field.value) {
          doc.text(`${field.name}: ${field.value}`, 10, y);
          y += lineHeight;
        }
      });
      y += 5;

      // Centered title: Cleaning Checklist
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor("#FF385C");
      const title = "CHECKLIST";
      const titleWidth = doc.getTextWidth(title);
      const pageWidth = doc.internal.pageSize.width;
      const centerX = (pageWidth - titleWidth) / 2;
      doc.text(title, centerX, y);
      y += 10;

      // Checklist
      doc.setFont("helvetica", "normal");
      checklist.forEach((room) => {
        if (y + 10 > pageHeight) {
          doc.addPage();
          y = 10;
        }

        // Section title with color
        const color = CATEGORY_COLORS[room.category] || "#000000";
        const rgb = hexToRgb(color);
        doc.setTextColor(rgb.r, rgb.g, rgb.b);
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.text(room.title, 10, y);
        y += 8;

        // Items
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        room.items.forEach((task) => {
          if (y + lineHeight > pageHeight) {
            doc.addPage();
            y = 10;
          }

          const prefix = task.checked ? "[+]" : "[  ]";
          const extraLabel = task.isDefault ? "" : " (extra)";
          doc.text(`${prefix} ${task.text}${extraLabel}`, 14, y);
          y += lineHeight;
        });

        y += 5;
      });

      doc.save(`Checklist_${dateNow}.pdf`);
    };

    setAlertVisible(true);
    setTimeout(() => setAlertVisible(false), 2000);
  };

  // Helper to convert hex color to RGB
  const hexToRgb = (hex: string) => {
    const bigint = parseInt(hex.replace("#", ""), 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    };
  };

  return (
    <Container component="section" maxWidth="md" sx={{ py: 4, px: { xs: 2, sm: 3 }, width: "100%", overflowX: "hidden" }}>
      <Grid container alignItems="center" spacing={{ xs: 2, sm: 4 }} paddingBottom={{ xs: 2, sm: 4 }}>
        {/* Left Side - Text */}
        <Grid item xs={12} md={6}>
          <Stack spacing={4}>
            <Typography variant="h2" fontWeight="bold">
              Customize Your AirBnB Cleaning Checklist
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Start with our professional AirBnB-ready checklist —
              covering all the essentials from arrival checks to spotless bathrooms.
              You can add your own items to fit your property’s unique needs,
              then download a personalized version to share with us or keep for records.
            </Typography>
            {/* Strengths List */}

          </Stack>
        </Grid>

        {/* Right Side - Image */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              aspectRatio: "1 / 1",
              height: "300px",
              backgroundImage: `url('https://djinni-assets.s3.us-east-1.amazonaws.com/how-clean/checklist_2.jpg')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: "8px",
              mx: "auto",
            }}
          />

        </Grid>
      </Grid>

      <Stack spacing={0}>
        {checklist.map((room, roomIndex) => (
          <Accordion key={room.title} defaultExpanded={false}>

            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: CATEGORY_COLORS[room.category] || "#ccc"
                  }}
                />
                <Typography variant="h6">{room.title}</Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
              <List>
                {room.items.map((item, itemIndex) => (
                  <ListItem
                    key={itemIndex}
                    secondaryAction={
                      !item.isDefault && (
                        <IconButton edge="end" onClick={() => handleDelete(roomIndex, itemIndex)}>
                          <DeleteIcon />
                        </IconButton>
                      )
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.checked}
                          onChange={() => handleToggle(roomIndex, itemIndex)}
                        />
                      }
                      label={
                        item.isDefault
                          ? item.text
                          : (
                            <>
                              {item.text}{" "}
                              <Typography variant="caption" color="text.secondary" component="span">
                                (extra)
                              </Typography>
                            </>
                          )
                      }
                    />
                  </ListItem>
                ))}
                <ListItem>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={newItems[room.title] || ""}
                    onChange={(e) =>
                      setNewItems({ ...newItems, [room.title]: e.target.value })
                    }
                    placeholder="Add extra item"
                  />
                  <IconButton edge="end" onClick={() => handleAdd(roomIndex)}>
                    <AddIcon />
                  </IconButton>
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
        Use this section to share access details, timing preferences, or special instructions.
      </Typography>

      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Additional Information (optional)</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 0.5, sm: 2 }, py: { xs: 1, sm: 2 } }}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Contact Person"
              value={clientInfo.contactPerson}
              onChange={(e) => setClientInfo({ ...clientInfo, contactPerson: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Phone"
              value={clientInfo.phone}
              onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              value={clientInfo.email}
              onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Property Address"
              value={clientInfo.property}
              onChange={(e) => setClientInfo({ ...clientInfo, property: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Job Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={clientInfo.jobDate}
              onChange={(e) => setClientInfo({ ...clientInfo, jobDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Start Time"
              type="time"
              InputLabelProps={{ shrink: true }}
              value={clientInfo.startTime}
              onChange={(e) => setClientInfo({ ...clientInfo, startTime: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="End Time"
              type="time"
              InputLabelProps={{ shrink: true }}
              value={clientInfo.endTime}
              onChange={(e) => setClientInfo({ ...clientInfo, endTime: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Access Code/Key"
              value={clientInfo.accessCode}
              onChange={(e) => setClientInfo({ ...clientInfo, accessCode: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Beds"
              type="number"
              value={clientInfo.beds}
              onChange={(e) => setClientInfo({ ...clientInfo, beds: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Baths"
              type="number"
              inputProps={{ step: "0.5" }}
              value={clientInfo.baths}
              onChange={(e) => setClientInfo({ ...clientInfo, baths: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Cleaning Type"
              value={clientInfo.cleaningType}
              onChange={(e) => setClientInfo({ ...clientInfo, cleaningType: e.target.value })}
              placeholder="e.g., Turnover, Deep Clean"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Package"
              select
              value={clientInfo.package}
              onChange={(e) => setClientInfo({ ...clientInfo, package: e.target.value })}
            >
              <MenuItem value="">Select Package</MenuItem>
              <MenuItem value="Standard">Standard</MenuItem>
              <MenuItem value="Professional">Professional</MenuItem>
              <MenuItem value="White-Glove">White-Glove</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              multiline
              rows={2}
              value={clientInfo.description}
              onChange={(e) => setClientInfo({ ...clientInfo, description: e.target.value })}
              placeholder="Add any special instructions or notes..."
            />
          </Grid>
          {customFields.map((field) => (
            <Grid item xs={12} key={field.id}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  label="Field Name"
                  value={field.name}
                  onChange={(e) => handleUpdateCustomField(field.id, 'name', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Field Value"
                  value={field.value}
                  onChange={(e) => handleUpdateCustomField(field.id, 'value', e.target.value)}
                  sx={{ flex: 2 }}
                />
                <IconButton
                  color="error"
                  onClick={() => handleDeleteCustomField(field.id)}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCustomField}
              size="small"
            >
              Add Custom Field
            </Button>
          </Grid>
        </Grid>
        </AccordionDetails>
      </Accordion>

      <Box display="flex" justifyContent="center" mt={4}>
        {alertVisible ? (
          <Alert severity="success" sx={{ width: 250, textAlign: 'center' }}>
            Downloading...
          </Alert>
        ) : (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
          >
            Download Checklist (PDF)
          </Button>
        )}
      </Box>

    </Container>
  );
}